import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  LifeBuoy, Plus, Send, Lightbulb, MessageSquare, ArrowLeft, Loader2, Search, Inbox,
} from "lucide-react";

import { AppShell } from "@/components/layout/AppShell";
import { PageHeader } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { fmtDateTime } from "@/lib/datetime";
import {
  supportService, TICKET_CATEGORIES, TICKET_STATUS_META, FEEDBACK_MODULES,
} from "@/services/supportService";
import { emitNotificationsChanged } from "@/services/notificationService";

const TABS = [
  ["get_help", "Get Help"],
  ["tickets", "My Requests"],
  ["features", "Feature Requests"],
];

const Field = ({ label, children }) => (
  <label className="block space-y-1.5">
    <span className="text-xs font-semibold text-brand-navy">{label}</span>
    {children}
  </label>
);

const selectCls = "w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-accent focus:outline-none";

/* ---------------- New ticket dialog ---------------- */
function NewTicketDialog({ open, onOpenChange, onCreated }) {
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("other");
  const [priority, setPriority] = useState("normal");
  const [busy, setBusy] = useState(false);
  const reset = () => { setSubject(""); setDescription(""); setCategory("other"); setPriority("normal"); };
  const submit = async () => {
    setBusy(true);
    try {
      const { ticket } = await supportService.createTicket({ subject, description, category, priority });
      toast.success(`Request ${ticket.ticket_number} submitted`);
      reset(); onOpenChange(false); onCreated?.(ticket); emitNotificationsChanged();
    } catch (e) { toast.error(e?.message || "Could not submit request"); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="new-ticket-dialog">
        <DialogHeader><DialogTitle>Submit a support request</DialogTitle>
          <DialogDescription>Tell us what's happening and we'll help you sort it out.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <Field label="Subject"><Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Short summary" data-testid="ticket-subject" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Category">
              <select className={selectCls} value={category} onChange={(e) => setCategory(e.target.value)} data-testid="ticket-category">
                {TICKET_CATEGORIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </Field>
            <Field label="Priority">
              <select className={selectCls} value={priority} onChange={(e) => setPriority(e.target.value)} data-testid="ticket-priority">
                <option value="normal">Normal</option>
                <option value="urgent">Urgent</option>
              </select>
            </Field>
          </div>
          <Field label="How can we help?"><Textarea rows={5} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Describe the issue or question…" data-testid="ticket-description" /></Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy || !subject || !description} className="bg-brand-navy hover:bg-brand-deep-blue" data-testid="ticket-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4" /> Submit</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ---------------- New feature request dialog ---------------- */
function NewFeatureDialog({ open, onOpenChange, onCreated }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [module, setModule] = useState("other");
  const [benefit, setBenefit] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await supportService.createFeatureRequest({ title, description, module, benefit });
      toast.success("Thanks! Your idea was submitted.");
      setTitle(""); setDescription(""); setModule("other"); setBenefit("");
      onOpenChange(false); onCreated?.();
    } catch (e) { toast.error(e?.message || "Could not submit idea"); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="new-feature-dialog">
        <DialogHeader><DialogTitle>Suggest a feature</DialogTitle>
          <DialogDescription>Tell us what would make running your business easier.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <Field label="Title"><Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="A short title for your idea" data-testid="feature-title" /></Field>
          <Field label="Area">
            <select className={selectCls} value={module} onChange={(e) => setModule(e.target.value)} data-testid="feature-module">
              {FEEDBACK_MODULES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </Field>
          <Field label="What would you like?"><Textarea rows={4} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Describe the feature…" data-testid="feature-description" /></Field>
          <Field label="Why would it help? (optional)"><Textarea rows={2} value={benefit} onChange={(e) => setBenefit(e.target.value)} placeholder="How would this help your business?" data-testid="feature-benefit" /></Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy || !title || !description} className="bg-brand-navy hover:bg-brand-deep-blue" data-testid="feature-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Lightbulb className="h-4 w-4" /> Submit idea</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ---------------- Ticket detail (thread + reply) ---------------- */
function TicketDetail({ ticketId, onBack }) {
  const [data, setData] = useState(null);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => supportService.getTicket(ticketId).then(setData).catch((e) => toast.error(e?.message)), [ticketId]);
  useEffect(() => { load(); }, [load]);
  if (!data) return <div className="py-16 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-muted-foreground" /></div>;
  const t = data.ticket;
  const meta = TICKET_STATUS_META[t.status] || {};
  const canReply = t.status !== "closed";
  const send = async () => {
    setBusy(true);
    try { const r = await supportService.replyTicket(ticketId, reply); setData(r); setReply(""); emitNotificationsChanged(); }
    catch (e) { toast.error(e?.message || "Could not send reply"); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-4" data-testid="ticket-detail">
      <button onClick={onBack} className="inline-flex items-center gap-1 text-sm text-accent hover:underline" data-testid="ticket-back"><ArrowLeft className="h-4 w-4" /> All requests</button>
      <div className="rounded-xl border border-border bg-white p-5">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <p className="font-brand text-lg font-bold text-brand-navy">{t.subject}</p>
            <p className="text-xs text-muted-foreground">{t.ticket_number} · {t.category_label}</p>
          </div>
          <Badge variant="outline" className={meta.cls} data-testid="ticket-status">{meta.label || t.status_label}</Badge>
        </div>
        <p className="mt-3 whitespace-pre-wrap rounded-lg bg-secondary/40 p-3 text-sm text-brand-charcoal">{t.description}</p>
      </div>

      <div className="space-y-3" data-testid="ticket-thread">
        {data.messages.filter((m) => m.kind !== "system_event").length === 0 && (
          <p className="text-center text-xs text-muted-foreground">No replies yet. We'll be in touch soon.</p>
        )}
        {data.messages.map((m) => (
          m.kind === "system_event" ? (
            <p key={m.id} className="text-center text-xs italic text-muted-foreground">{m.body} · {fmtDateTime(m.created_at)}</p>
          ) : (
            <div key={m.id} className={`max-w-[85%] rounded-xl border p-3 ${m.author_role === "staff" ? "ml-auto border-brand-navy/15 bg-brand-navy/5" : "border-border bg-white"}`} data-testid={`ticket-msg-${m.author_role}`}>
              <p className="text-xs font-semibold text-brand-navy">{m.author_role === "staff" ? m.author_name : "You"}</p>
              <p className="mt-1 whitespace-pre-wrap text-sm text-brand-charcoal">{m.body}</p>
              <p className="mt-1 text-[11px] text-muted-foreground">{fmtDateTime(m.created_at)}</p>
            </div>
          )
        ))}
      </div>

      {canReply ? (
        <div className="rounded-xl border border-border bg-white p-4">
          <Textarea rows={3} value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Write a reply…" data-testid="ticket-reply-input" />
          <div className="mt-2 flex justify-end">
            <Button onClick={send} disabled={busy || !reply.trim()} className="bg-brand-navy hover:bg-brand-deep-blue" data-testid="ticket-reply-send">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4" /> Send reply</>}
            </Button>
          </div>
        </div>
      ) : (
        <p className="rounded-xl border border-dashed border-border bg-secondary/30 p-4 text-center text-sm text-muted-foreground">This request is closed. Start a new request if you need more help.</p>
      )}
    </div>
  );
}

/* ---------------- General feedback card ---------------- */
function FeedbackCard() {
  const [message, setMessage] = useState("");
  const [sentiment, setSentiment] = useState("neutral");
  const [module, setModule] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try { await supportService.sendFeedback({ message, sentiment, module: module || undefined }); toast.success("Thanks for the feedback!"); setMessage(""); setSentiment("neutral"); setModule(""); }
    catch (e) { toast.error(e?.message || "Could not send feedback"); } finally { setBusy(false); }
  };
  const opts = [["positive", "Positive"], ["neutral", "Neutral"], ["needs_improvement", "Needs Improvement"]];
  return (
    <div className="rounded-xl border border-border bg-white p-5" data-testid="feedback-card">
      <p className="flex items-center gap-2 font-semibold text-brand-navy"><MessageSquare className="h-4 w-4 text-accent" /> Share feedback</p>
      <p className="mt-0.5 text-sm text-muted-foreground">Tell us how AgentHQ is working for you.</p>
      <div className="mt-3 flex gap-2">
        {opts.map(([v, l]) => (
          <button key={v} onClick={() => setSentiment(v)} data-testid={`feedback-sentiment-${v}`}
            className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${sentiment === v ? "border-brand-navy bg-brand-navy text-white" : "border-border hover:border-accent"}`}>{l}</button>
        ))}
      </div>
      <select className={`${selectCls} mt-3`} value={module} onChange={(e) => setModule(e.target.value)} data-testid="feedback-module">
        <option value="">General (no specific area)</option>
        {FEEDBACK_MODULES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
      <Textarea className="mt-3" rows={3} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Your feedback…" data-testid="feedback-message" />
      <div className="mt-2 flex justify-end">
        <Button onClick={submit} disabled={busy || !message.trim()} variant="outline" data-testid="feedback-submit">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send feedback"}
        </Button>
      </div>
    </div>
  );
}

/* ---------------- Main page ---------------- */
export default function HelpSupport() {
  const [params, setParams] = useSearchParams();
  const initialTab = params.get("tab") === "features" ? "features" : params.get("ticket") ? "tickets" : "get_help";
  const [tab, setTab] = useState(initialTab);
  const [tickets, setTickets] = useState(null);
  const [features, setFeatures] = useState(null);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [selected, setSelected] = useState(params.get("ticket") || null);
  const [ticketOpen, setTicketOpen] = useState(false);
  const [featureOpen, setFeatureOpen] = useState(false);

  const loadTickets = useCallback(() => {
    const p = {};
    if (q) p.q = q;
    if (statusFilter) p.status = statusFilter;
    supportService.listTickets(p).then((d) => setTickets(d.tickets)).catch(() => setTickets([]));
  }, [q, statusFilter]);
  const loadFeatures = useCallback(() => supportService.listFeatureRequests().then((d) => setFeatures(d.feature_requests)).catch(() => setFeatures([])), []);

  useEffect(() => { if (tab === "tickets") loadTickets(); }, [tab, loadTickets]);
  useEffect(() => { if (tab === "features") loadFeatures(); }, [tab, loadFeatures]);

  const goTab = (t) => { setTab(t); setSelected(null); setParams(t === "get_help" ? {} : { tab: t }); };
  const openTicket = (id) => { setSelected(id); setParams({ ticket: id }); };

  return (
    <AppShell>
      <div className="space-y-6" data-testid="help-support-page">
        <PageHeader eyebrow="AgentHQ · Support" title="Help & Support" description="Get help, track your requests, and share ideas — all inside AgentHQ." />

        <div className="flex flex-wrap gap-2">
          {TABS.map(([v, l]) => (
            <button key={v} onClick={() => goTab(v)} data-testid={`help-tab-${v}`}
              className={`rounded-full border px-4 py-1.5 text-sm font-medium transition-colors ${tab === v ? "border-brand-navy bg-brand-navy text-white" : "border-border hover:border-accent"}`}>{l}</button>
          ))}
        </div>

        {/* GET HELP */}
        {tab === "get_help" && (
          <div className="grid gap-4 lg:grid-cols-2" data-testid="help-get-help">
            <div className="rounded-xl border border-brand-navy/15 bg-gradient-to-br from-brand-navy to-brand-deep-blue p-6 text-white">
              <LifeBuoy className="h-7 w-7 text-brand-gold" />
              <p className="mt-3 font-brand text-xl font-bold">Need a hand? We're here to help.</p>
              <p className="mt-1 text-sm text-white/70">Submit a request and keep the conversation organized right inside AgentHQ.</p>
              <div className="mt-4 flex flex-wrap gap-2">
                <Button onClick={() => setTicketOpen(true)} className="bg-brand-gold text-brand-navy hover:bg-brand-soft-gold" data-testid="open-new-ticket"><Plus className="h-4 w-4" /> New support request</Button>
                <Button onClick={() => setFeatureOpen(true)} variant="outline" className="border-white/30 bg-white/10 text-white hover:bg-white/20" data-testid="open-new-feature"><Lightbulb className="h-4 w-4" /> Suggest a feature</Button>
              </div>
            </div>
            <FeedbackCard />
          </div>
        )}

        {/* MY REQUESTS */}
        {tab === "tickets" && (selected ? (
          <TicketDetail ticketId={selected} onBack={() => { setSelected(null); setParams({ tab: "tickets" }); loadTickets(); }} />
        ) : (
          <div className="space-y-4" data-testid="help-tickets">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex flex-wrap gap-2">
                <div className="relative"><Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search ticket # or subject…" className="w-64 pl-8" data-testid="ticket-search" /></div>
                <select className={`${selectCls} w-44`} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="ticket-status-filter">
                  <option value="">All statuses</option>
                  {Object.entries(TICKET_STATUS_META).map(([v, m]) => <option key={v} value={v}>{m.label}</option>)}
                </select>
              </div>
              <Button onClick={() => setTicketOpen(true)} className="bg-brand-navy hover:bg-brand-deep-blue" data-testid="tickets-new"><Plus className="h-4 w-4" /> New request</Button>
            </div>
            {!tickets ? <div className="py-16 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-muted-foreground" /></div> :
              tickets.length === 0 ? (
                <div className="flex flex-col items-center gap-2 rounded-xl border border-dashed border-border bg-white py-16 text-center" data-testid="tickets-empty">
                  <div className="grid h-12 w-12 place-items-center rounded-full bg-secondary text-muted-foreground"><Inbox className="h-5 w-5" /></div>
                  <p className="text-sm font-semibold">No support requests yet.</p>
                  <p className="text-xs text-muted-foreground">If you need help with AgentHQ, start a new request.</p>
                </div>
              ) : (
                <div className="overflow-hidden rounded-xl border border-border bg-white">
                  {tickets.map((t) => {
                    const meta = TICKET_STATUS_META[t.status] || {};
                    return (
                      <button key={t.id} onClick={() => openTicket(t.id)} className="flex w-full items-center gap-3 border-b border-border px-4 py-3 text-left last:border-0 hover:bg-secondary/40" data-testid="ticket-row">
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-semibold text-brand-navy">{t.subject}</p>
                          <p className="text-xs text-muted-foreground">{t.ticket_number} · {t.category_label} · updated {fmtDateTime(t.updated_at)}</p>
                        </div>
                        <Badge variant="outline" className={meta.cls}>{meta.label || t.status_label}</Badge>
                      </button>
                    );
                  })}
                </div>
              )}
          </div>
        ))}

        {/* FEATURE REQUESTS */}
        {tab === "features" && (
          <div className="space-y-4" data-testid="help-features">
            <div className="flex items-center justify-between gap-3 rounded-xl border border-border bg-white p-4">
              <div>
                <p className="font-semibold text-brand-navy">Have an idea for AgentHQ?</p>
                <p className="text-sm text-muted-foreground">Tell us what would make running your real estate business easier.</p>
              </div>
              <Button onClick={() => setFeatureOpen(true)} className="bg-brand-navy hover:bg-brand-deep-blue" data-testid="features-new"><Lightbulb className="h-4 w-4" /> Suggest a feature</Button>
            </div>
            {!features ? <div className="py-16 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-muted-foreground" /></div> :
              features.length === 0 ? (
                <p className="rounded-xl border border-dashed border-border bg-white py-12 text-center text-sm text-muted-foreground" data-testid="features-empty">You haven't submitted any ideas yet.</p>
              ) : (
                <div className="space-y-2">
                  {features.map((f) => (
                    <div key={f.id} className="rounded-xl border border-border bg-white p-4" data-testid="feature-row">
                      <div className="flex flex-wrap items-start justify-between gap-2">
                        <p className="font-semibold text-brand-navy">{f.title}</p>
                        <Badge variant="outline" className="border-accent/40 text-accent">{f.status_label}</Badge>
                      </div>
                      <p className="mt-1 text-sm text-muted-foreground">{f.description}</p>
                      <p className="mt-1 text-[11px] text-muted-foreground">Submitted {fmtDateTime(f.created_at)}</p>
                    </div>
                  ))}
                </div>
              )}
          </div>
        )}
      </div>

      <NewTicketDialog open={ticketOpen} onOpenChange={setTicketOpen} onCreated={() => { setTab("tickets"); setParams({ tab: "tickets" }); loadTickets(); }} />
      <NewFeatureDialog open={featureOpen} onOpenChange={setFeatureOpen} onCreated={() => { setTab("features"); setParams({ tab: "features" }); loadFeatures(); }} />
    </AppShell>
  );
}
